import { FLOW_VERSION } from "./flowConstants";
import { validateFlow } from "./flowValidation";
import { applyAutoMappingToFlow } from "./flowAutoMapping";

let _uid = 0;
export const uid = () => `_uid_${Date.now()}_${++_uid}`;

const LETTER_SUFFIXES = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

/**
 * Generate a slug-safe screen ID from a title string.
 * Only A-Z and _ allowed (no numbers).
 */
export function slugify(text) {
  return text
    .toUpperCase()
    .replace(/[0-9]+/g, "")
    .replace(/[^A-Z]+/g, "_")
    .replace(/^_|_$/g, "")
    .replace(/_+/g, "_")
    .slice(0, 30);
}

/**
 * Generate a unique screen ID from a title, avoiding duplicates.
 * Uses letter suffixes (_A, _B, ...) to avoid numbers in IDs.
 */
export function generateScreenId(title, existingIds = []) {
  let base = title ? slugify(title) : "SCREEN";
  if (!base) base = "SCREEN";
  let id = base;
  let idx = 0;
  while (existingIds.includes(id)) {
    if (idx < LETTER_SUFFIXES.length) {
      id = `${base}_${LETTER_SUFFIXES[idx]}`.slice(0, 30);
    } else {
      id = `${base}_${LETTER_SUFFIXES[Math.floor(idx / 26)]}${LETTER_SUFFIXES[idx % 26]}`.slice(0, 30);
    }
    idx++;
  }
  return id;
}

/**
 * Generate a field name from a label string.
 */
export function generateFieldName(label, existingNames = []) {
  let base = (label || "field")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "")
    .slice(0, 30);
  if (!base) base = "field";
  let name = base;
  let counter = 1;
  while (existingNames.includes(name)) {
    name = `${base}_${counter}`.slice(0, 30);
    counter++;
  }
  return name;
}

/**
 * Create a blank screen object.
 */
export function createBlankScreen(existingIds = []) {
  const id = generateScreenId("NEW_SCREEN", existingIds);
  return {
    _uid: uid(),
    id,
    title: "New Screen",
    data: {},
    terminal: false,
    layout: {
      type: "SingleColumnLayout",
      children: [
        {
          _uid: uid(),
          type: "Form",
          name: `${id.toLowerCase()}_form`,
          children: [],
        },
      ],
    },
  };
}

/**
 * Create a blank component of the given type.
 */
export function createComponent(type, existingNames = []) {
  const base = { _uid: uid(), type };

  switch (type) {
    case "TextInput":
      return {
        ...base,
        name: generateFieldName("text_input", existingNames),
        label: "Text Input",
        "input-type": "text",
        required: false,
      };
    case "Dropdown":
      return {
        ...base,
        name: generateFieldName("dropdown", existingNames),
        label: "Select Option",
        "data-source": [
          { id: "option_1", title: "Option 1" },
          { id: "option_2", title: "Option 2" },
        ],
      };
    case "RadioButtonsGroup":
      return {
        ...base,
        name: generateFieldName("radio", existingNames),
        label: "Choose One",
        "data-source": [
          { id: "option_1", title: "Option 1" },
          { id: "option_2", title: "Option 2" },
        ],
      };
    case "CheckboxGroup":
      return {
        ...base,
        name: generateFieldName("checkbox", existingNames),
        label: "Select Multiple",
        "data-source": [
          { id: "option_1", title: "Option 1" },
          { id: "option_2", title: "Option 2" },
        ],
      };
    case "DatePicker":
      return {
        ...base,
        name: generateFieldName("date", existingNames),
        label: "Select Date",
      };
    case "TextHeading":
      return { ...base, text: "Heading text" };
    case "TextSubheading":
      return { ...base, text: "Subheading text" };
    case "TextBody":
      return { ...base, text: "Body text goes here." };
    case "TextCaption":
      return { ...base, text: "Small helper text" };
    case "RichText":
      return { ...base, text: "*Important:* Please review carefully." };
    case "Image":
      return { ...base, src: "", height: 200 };
    case "Footer":
      return {
        ...base,
        label: "Next",
        "on-click-action": {
          name: "navigate",
          next: { type: "screen", name: "" },
          payload: {},
        },
      };
    default:
      return base;
  }
}

/**
 * Create a default starter flow with one screen and a terminal.
 */
export function createStarterFlow() {
  return {
    version: FLOW_VERSION,
    screens: [
      {
        _uid: uid(),
        id: "USER_DETAILS",
        title: "User Details",
        data: {},
        terminal: false,
        layout: {
          type: "SingleColumnLayout",
          children: [
            { _uid: uid(), type: "TextHeading", text: "Enter your details" },
            { _uid: uid(), type: "TextSubheading", text: "Please provide your basic information." },
            {
              _uid: uid(),
              type: "Form",
              name: "user_details_form",
              children: [
                {
                  _uid: uid(),
                  type: "TextInput",
                  name: "full_name",
                  label: "Full Name",
                  "input-type": "text",
                  required: true,
                },
                {
                  _uid: uid(),
                  type: "TextInput",
                  name: "email",
                  label: "Email",
                  "input-type": "email",
                  required: true,
                },
                {
                  _uid: uid(),
                  type: "Footer",
                  label: "Next",
                  "on-click-action": {
                    name: "navigate",
                    next: { type: "screen", name: "CONFIRMATION" },
                    payload: {
                      full_name: "\\${form.full_name}",
                      email: "\\${form.email}",
                    },
                  },
                },
              ],
            },
          ],
        },
      },
      {
        _uid: uid(),
        id: "CONFIRMATION",
        title: "Confirm",
        data: {
          full_name: { type: "string" },
          email: { type: "string" },
        },
        terminal: true,
        layout: {
          type: "SingleColumnLayout",
          children: [
            { _uid: uid(), type: "TextHeading", text: "Review and submit" },
            {
              _uid: uid(),
              type: "Footer",
              label: "Submit",
              "on-click-action": {
                name: "complete",
                payload: {
                  full_name: "\\${data.full_name}",
                  email: "\\${data.email}",
                },
              },
            },
          ],
        },
      },
    ],
  };
}

/**
 * Strip internal _uid keys from flow state and return clean Meta-compatible JSON.
 */
export function buildFlowJSON(flowState) {
  // Clone first, apply auto mapping, then strip internal keys.
  const cloned = JSON.parse(JSON.stringify(flowState));
  const mapped = applyAutoMappingToFlow(cloned);
  return JSON.parse(
    JSON.stringify(mapped, (key, value) => {
      if (key === "_uid") return undefined;
      if (key === "placeholder") return undefined;
      return value;
    })
  );
}

/**
 * Build and validate. Returns { json, validation }.
 */
export function exportFlowJSON(flowState) {
  const json = buildFlowJSON(flowState);
  const validation = validateFlow(json);
  return { json, validation };
}
